import { DEFAULT_ALLOWED_NETWORKS, hostHeaderName, isLocalName, parseAllowedNetworks } from "odyssey-shared";

/**
 * The networks a page wearing a trial look may be served from: the development servers' own local networks
 * (`DEFAULT_ALLOWED_NETWORKS`: loopback, `10.0.0.0/8`, `192.168.0.0/16`, link-local and IPv6 unique local) and the
 * remaining private IPv4 range, `172.16.0.0/12`. The servers leave that range out because a tunnel's connector reaches
 * them from a Docker bridge there; here the name is the one in the address bar, and a page reached through a tunnel or
 * published anywhere is named by a domain, never by a private address.
 */
const TRIAL_NETWORKS = parseAllowedNetworks(`${DEFAULT_ALLOWED_NETWORKS},172.16.0.0/12`);

/**
 * Whether the page wears a trial look (P1.9-T88): only when `?look=` names one and the page is served by a development
 * server on this machine or this local network, which is told by its address: `localhost`, or a literal loopback,
 * private or link-local address, IPv4 or IPv6. A page named by a domain, the published one included, or by a public
 * address ignores `?look=` and never fetches a trial sheet, which exists on no host but a developer's machine.
 *
 * @param look The page's `?look=` value, or null when it has none.
 * @param hostname The page's `location.hostname`; an IPv6 literal carries its brackets.
 * @returns True when the trial look's code loads.
 * @example
 * trialLookAllowed("mana-seed", "192.168.10.110"); // true
 * trialLookAllowed("mana-seed", "[::1]"); // true
 * trialLookAllowed("mana-seed", "play.odysseytales.net"); // false
 */
export function trialLookAllowed(look: string | null, hostname: string): boolean {
  return look !== null && look !== "" && localAddress(hostname);
}

/**
 * Whether a page is served at a local address: `localhost`, or a literal loopback, private or link-local address. The
 * trial look and the character sprite viewer (P1.9-T93) both read trial art only on such a page.
 *
 * @param hostname The page's `location.hostname`; an IPv6 literal carries its brackets.
 * @returns True for a local address; false for any domain and any public address.
 */
export function localAddress(hostname: string): boolean {
  const name = hostHeaderName(hostname);
  return name !== null && isLocalName(name, TRIAL_NETWORKS, "");
}
